import type {
  OddsHistoryPeerBook,
  OddsHistoryQuoteCapacity,
} from "@/components/odds/odds-history-selection";
import type { BetLinkSet } from "@/lib/bet-links";
import { getBookDisplayName } from "@/lib/utils/sports-books";

type OverlayPeerQuote = {
  book: string;
  bookDisplayName?: string;
  selectionId: string | null | undefined;
  line: number | null;
  oddsAmerican: number | null | undefined;
  betLinks?: BetLinkSet | null;
  capacity?: OddsHistoryQuoteCapacity | null;
  isBestPrice?: true;
};

type ValidQuote = OverlayPeerQuote & { oddsAmerican: number };

export function buildOverlayPeersFromQuotes(
  quotes: readonly OverlayPeerQuote[],
): OddsHistoryPeerBook[] {
  const order: string[] = [];
  const byBook = new Map<
    string,
    { chart?: ValidQuote; flagged: ValidQuote[]; fallback?: ValidQuote }
  >();
  for (const quote of quotes) {
    if (
      !quote.book ||
      typeof quote.oddsAmerican !== "number" ||
      !Number.isFinite(quote.oddsAmerican)
    ) {
      continue;
    }
    const valid = quote as ValidQuote;
    let slots = byBook.get(quote.book);
    if (!slots) {
      slots = { flagged: [] };
      byBook.set(quote.book, slots);
      order.push(quote.book);
    }
    if (valid.selectionId && !slots.chart) {
      slots.chart = valid;
    }
    if (valid.isBestPrice === true) {
      slots.flagged.push(valid);
    }
    if (!slots.fallback) {
      slots.fallback = valid;
    }
  }
  const toPeer = (quote: ValidQuote): OddsHistoryPeerBook => ({
    book: quote.book,
    bookDisplayName: quote.bookDisplayName ?? getBookDisplayName(quote.book),
    ...(quote.selectionId ? { selectionId: quote.selectionId } : {}),
    line: quote.line,
    oddsAmerican: quote.oddsAmerican,
    ...(quote.betLinks ? { betLinks: quote.betLinks } : {}),
    ...(quote.capacity ? { capacity: quote.capacity } : {}),
    ...(quote.isBestPrice === true ? { isBestPrice: true as const } : {}),
  });
  const out: OddsHistoryPeerBook[] = [];
  for (const book of order) {
    const { chart, flagged, fallback } = byBook.get(book)!;
    if (chart) {
      out.push(toPeer(chart));
    }
    for (const candidate of flagged) {
      if (candidate !== chart) {
        out.push(toPeer({ ...candidate, selectionId: undefined }));
      }
    }
    if (!chart && flagged.length === 0 && fallback) {
      out.push(toPeer(fallback));
    }
  }
  return out;
}
